/** Приложения и боты: раздел «Приложения» и OAuth2-авторизация бота на сервер (docs/api/openapi.yaml). */
import { authed } from './auth'
import type { Application, AuthorizeInfo, GuildPartial, Member } from '@/types/api'

export interface ApplicationPatch {
  name?: string
  description?: string
  bot_public?: boolean
  bot_require_code_grant?: boolean
  redirect_uris?: string[]
  privileged_intents?: number
}

export interface AuthorizeParams {
  clientId: string
  scope: string
  permissions: string
  guildId: string | null
  /** Вход через Кличат (OAuth2 authorization code): куда вернуть пользователя с кодом. */
  responseType?: string
  redirectUri?: string
  state?: string
  codeChallenge?: string
}

/** Биты привилегированных intents (как у Discord). */
export const PRIVILEGED_INTENTS: { bit: number; label: string; hint: string }[] = [
  { bit: 1 << 1, label: 'Участники сервера (GUILD_MEMBERS)', hint: 'События входа и выхода участников, список участников в GUILD_CREATE и GET /guilds/{id}/members' },
  { bit: 1 << 8, label: 'Присутствие (GUILD_PRESENCES)', hint: 'PRESENCE_UPDATE и статусы участников в GUILD_CREATE' },
  { bit: 1 << 15, label: 'Содержимое сообщений (MESSAGE_CONTENT)', hint: 'Текст, вложения и превью в сообщениях, где бот не упомянут. Нужно для префикс-команд' },
]

const authorizeQuery = (p: AuthorizeParams) => {
  const q = new URLSearchParams({ client_id: p.clientId, scope: p.scope || 'bot', permissions: p.permissions || '0' })
  if (p.guildId) q.set('guild_id', p.guildId)
  if (p.responseType) q.set('response_type', p.responseType)
  if (p.redirectUri) q.set('redirect_uri', p.redirectUri)
  if (p.state) q.set('state', p.state)
  if (p.codeChallenge) {
    q.set('code_challenge', p.codeChallenge)
    q.set('code_challenge_method', 'S256')
  }
  return q.toString()
}

export const applicationsApi = {
  list: () => authed<Application[]>('/applications'),
  create: (name: string, description: string) =>
    authed<{ application: Application; token: string }>('/applications', { method: 'POST', body: JSON.stringify({ name, description, developer_agreement: true }) }),
  get: (id: string) => authed<Application>(`/applications/${id}`),
  update: (id: string, patch: ApplicationPatch) => authed<Application>(`/applications/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) => authed<void>(`/applications/${id}`, { method: 'DELETE' }),
  resetToken: (id: string) => authed<{ token: string }>(`/applications/${id}/bot/reset`, { method: 'POST' }),
  resetSecret: (id: string) => authed<{ client_secret: string }>(`/applications/${id}/secret/reset`, { method: 'POST' }),
  guilds: (id: string) => authed<GuildPartial[]>(`/applications/${id}/guilds`),
  authorizeInfo: (p: AuthorizeParams) => authed<AuthorizeInfo>(`/oauth2/authorize?${authorizeQuery(p)}`),
  authorize: (p: AuthorizeParams, guildId: string) =>
    authed<{ guild_id: string; permissions: string; member: Member; location: string }>(`/oauth2/authorize?${authorizeQuery(p)}`, {
      method: 'POST',
      body: JSON.stringify({ guild_id: guildId, permissions: p.permissions || '0', authorize: true }),
    }),
  /** Вход через Кличат: пользователь соглашается, приложение получает код на свой адрес. */
  authorizeCode: (p: AuthorizeParams) =>
    authed<{ location: string }>(`/oauth2/authorize?${authorizeQuery(p)}`, { method: 'POST', body: JSON.stringify({ authorize: true }) }),
}

/** Ссылка «Добавить бота на сервер» в формате Discord — её же собирает generateInvite в discord.js. */
export function inviteLinkFor(clientId: string, permissions: bigint): string {
  return `${location.origin}/oauth2/authorize?client_id=${clientId}&scope=bot%20applications.commands&permissions=${permissions}`
}
